import { useTranslations } from '@vocab/react';
import { useToast } from 'braid-design-system';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CAREER_HISTORY } from 'src/components/Router';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { ADD, EDIT } from 'src/consts';
import { YEARS_OF_EXPERIENCE_FORM } from 'src/context/FormContext';
import { analyticsClient } from 'src/modules/analytics';
import { yearsOfExperienceUpdate } from 'src/modules/analytics/yearsOfExperience/actionCreators';
import { useMutation, useQuery } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import { useFormDirtyStateUpdates } from 'src/modules/detect-unsaved-changes/useFormDirtyStateUpdates';
import {
  GetYearsOfExperienceDocument,
  UpdateYearsOfExperienceDocument,
  type UpdateYearsOfExperienceMutation,
} from 'src/types/graphql';

import translations from './.vocab';
import {
  YearsOfExperienceForm,
  type YearsOfExperience,
} from './YearsOfExperienceForm';

interface EditYearsOfExperienceProps {
  displayDialog: boolean;
  closeYearsOfExperienceDialog: () => void;
  setDisplayYearsOfExperienceDialog: (open: boolean) => void;
}

export const EditYearsOfExperience = ({
  displayDialog,
  closeYearsOfExperienceDialog,
  setDisplayYearsOfExperienceDialog,
}: EditYearsOfExperienceProps) => {
  const { t } = useTranslations(translations);
  const { language } = useAppConfig();
  const dispatch = useDispatch();
  const navigate = useNavigateRoute();

  const showToast = useToast();
  const { data, loading, error } = useQuery(GetYearsOfExperienceDocument, {
    variables: { languageCode: language },
  });

  const yearsOfExperience =
    loading || error ? null : data?.viewer?.yearsOfExperience;

  const [initialYearsOfExperience] = useState(yearsOfExperience);

  const updateFormDirtyState = useFormDirtyStateUpdates({
    form: YEARS_OF_EXPERIENCE_FORM,
  });

  const onFormDirtyChange = useCallback(
    (dirty: boolean) => {
      updateFormDirtyState({ dirty });
    },
    [updateFormDirtyState],
  );

  const mutationSuccess = useCallback(
    (mutationResponse: UpdateYearsOfExperienceMutation) => {
      setDisplayYearsOfExperienceDialog(false);

      showToast({
        message: t('Years of experience updated'),
        tone: 'positive',
        key: 'yearsOfExperience',
      });

      const mutationData =
        mutationResponse.updateYearsOfExperience.viewer?.yearsOfExperience;

      if (
        !Boolean(initialYearsOfExperience?.option?.id) &&
        Boolean(mutationData?.option?.id) &&
        mutationData?.newToWorkforce === false
      ) {
        analyticsClient.cardOpened({
          currentPageSection: 'career history',
          currentPageSectionState: 'add',
        });
        navigate({
          card: CAREER_HISTORY,
          mode: EDIT,
        });
      }
    },
    [
      initialYearsOfExperience?.option?.id,
      setDisplayYearsOfExperienceDialog,
      showToast,
      t,
      navigate,
    ],
  );

  const [
    updateYearsOfExperienceInput,
    { loading: mutationLoading, error: mutationError },
  ] = useMutation(UpdateYearsOfExperienceDocument, {
    onCompleted: mutationSuccess,
  });

  const submitForm = useCallback(
    (params: { dirty: boolean; formValues: YearsOfExperience }): void => {
      const { dirty, formValues } = params;

      if (dirty && !mutationLoading) {
        updateYearsOfExperienceInput({
          variables: {
            id: formValues?.option?.id || null,
            newToWorkforce: formValues.newToWorkforce,
            languageCode: language,
          },
        });

        dispatch(
          yearsOfExperienceUpdate({
            actionName: !Boolean(yearsOfExperience?.option?.id) ? ADD : EDIT,
            userIsNewToWorkforce: formValues.newToWorkforce,
          }),
        );
      } else {
        closeYearsOfExperienceDialog();
      }
    },
    [
      closeYearsOfExperienceDialog,
      dispatch,
      language,
      mutationLoading,
      updateYearsOfExperienceInput,
      yearsOfExperience?.option?.id,
    ],
  );

  const onCancel = () => {
    closeYearsOfExperienceDialog();
  };

  return (
    <YearsOfExperienceForm
      initialValues={yearsOfExperience || undefined}
      onFormDirtyChange={onFormDirtyChange}
      submitForm={submitForm}
      onCancel={onCancel}
      displayDialog={displayDialog}
      closeYearsOfExperienceDialog={closeYearsOfExperienceDialog}
      isLoading={mutationLoading}
      hasError={Boolean(mutationError)}
      hasYearsOfExperience={Boolean(yearsOfExperience?.option?.id)}
    />
  );
};
